// chatLayoutAnimation.js
// ─────────────────────────────────────────────────────────────────────────────
// Presentational FLIP engine for the Whisper message stack.
//
// Everything here is visual-only: it never touches message state, sockets,
// send queues, or reliability logic, and every entry point is wrapped so a
// failure degrades to the non-animated baseline.
//
// Architecture (React wiring lives in ChatLayoutTransition in ChatPage.js):
//  • FIRST rects are captured in getSnapshotBeforeUpdate — after render but
//    BEFORE React mutates the DOM — so they are the CURRENT VISUAL positions,
//    including any transform from an animation still in flight. A snapshot is
//    used once, in the same commit, then discarded: it can never go stale.
//  • Before measuring LAST rects, in-flight outer animations are cancel()ed.
//    cancel() removes the animation's effect entirely — no fill, no inline
//    style, no commitStyles residue — so rows revert to natural layout for the
//    measurement, while the FIRST rects (captured pre-cancel) preserve where
//    the user actually saw them. Rapid events therefore retarget from exactly
//    the visual position, never stack a second transform on the first.
//  • OUTER row ([data-chat-layout-row]) animates layout deltas only; the INNER
//    wrapper animates entry only. The two layers never share a transform.
//  • Message rows keep ONE stable identity (client_temp_id-first) as both their
//    React key and animation key, so the temp→real id swap on server ACK merges
//    into the SAME DOM node — animations are never interrupted by remounts.
//  • Entry eligibility is EXPLICIT: the insertion code paths register
//    'outgoing-new' / 'incoming-new' per stable key and the coordinator consumes
//    each registration exactly once. No timing heuristics.
//  • Only rows within the viewport (± buffer) are measured; reads and writes
//    are phase-separated to avoid layout thrash.
// ─────────────────────────────────────────────────────────────────────────────

export const CHAT_ANIM = {
  // Snappier timings: the motion reads faster on slow devices while keeping
  // the same WAAPI-based, GPU-only transforms (no layout work per frame).
  STACK_MS: 320,          // outer layout FLIP when a message is inserted
  ENTRY_OUT_MS: 440,      // inner entry: new outgoing bubble (the hero — slowest)
  ENTRY_IN_MS: 380,       // inner entry: new incoming bubble
  // The bubble waits this long when the stack actually has to glide out of its
  // slot first — the room opens, THEN the bubble pops in (WhatsApp sequencing).
  ENTRY_CLEAR_DELAY_MS: 70,
  TYPING_STACK_MS: 300,   // outer layout FLIP when typing toggles
  TYPING_ENTRY_MS: 280,   // typing indicator inner entry
  TYPING_EXIT_MS: 180,    // typing indicator exit ghost fade
  // easeOutQuart: gentler initial velocity than the previous curve → the whole
  // motion reads calmer/smoother while staying decisive.
  EASING: 'cubic-bezier(0.25, 1, 0.5, 1)',
  STAGGER_MS: 35,         // per-bubble delay within one same-commit batch
  STAGGER_MAX: 4,         // cap on staggered bubbles per batch
  // "Near bottom" measured BEFORE the mutation. The legacy auto-scroll effect
  // checks distance < 150 AFTER the new row already grew scrollHeight, which is
  // roughly ≤100 pre-mutation for a typical row — same user intent, one number.
  NEAR_BOTTOM_PX: 100,
  VIEWPORT_BUFFER_PX: 120, // measure rows within this margin of the viewport
  // Entry pose: the bubble EMERGES near its final slot — a short rise plus a
  // slight growth from its tail corner. The stack FLIP carries the long-range
  // displacement, so the inner rise is CLAMPED and never becomes a
  // composer-to-slot flight, no matter how far the composer really is.
  ENTRY_OUT_START_Y_MIN: 18,  // px clamp for the outgoing rise
  ENTRY_OUT_START_Y_MAX: 28,
  ENTRY_OUT_START_Y: 24,      // fallback when the composer can't be measured
  ENTRY_IN_START_Y: 18,       // px, incoming rise (never composer-derived)
  ENTRY_OUT_START_SCALE: 0.86,
  ENTRY_IN_START_SCALE: 0.9,
  // Older in-flight entries gently speed up (never snap/finish instantly) when
  // a newer message starts, so rapid sends stay a continuous flow.
  ENTRY_RETARGET_RATE: 2,
};

export const ROW_SELECTOR = '[data-chat-layout-row]';
export const TYPING_ROW_KEY = '__whisper_typing_indicator__';

const DEBUG_ANIM =
  process.env.NODE_ENV !== 'production' &&
  process.env.REACT_APP_DEBUG_MESSAGE_ANIMATIONS === 'true';

const dbg = (...args) => {
  if (!DEBUG_ANIM) return;
  try { console.debug('[chat-anim]', ...args); } catch { /* noop */ }
};

const supportsWAAPI = (el) => Boolean(el) && typeof el.animate === 'function';

// ── Animation registries ─────────────────────────────────────────────────────
// key -> { animation, frames, opts, startedAt, el }. Records (not just Animation
// objects) so an interrupted row can resume on a replacement DOM node.
const outerAnims = new Map();
const entryAnims = new Map();
let activeGhost = null;

const makeRecord = (registry, el, key, frames, opts, label) => {
  const animation = el.animate(frames, opts);
  const record = { animation, frames, opts, startedAt: performance.now(), el };
  registry.set(key, record);
  animation.onfinish = () => {
    // cancel() drops the finished effect so nothing lingers; the element's
    // natural style equals the final keyframe, so nothing visibly moves.
    try { animation.cancel(); } catch { /* noop */ }
    if (registry.get(key) === record) registry.delete(key);
    dbg(`${label}_FINISH`, { key });
  };
  animation.oncancel = () => {
    if (registry.get(key) === record) registry.delete(key);
  };
  return record;
};

// ── Explicit entry-animation eligibility ─────────────────────────────────────
// The insertion code paths REGISTER a message at the exact moment it genuinely
// enters the open conversation: the optimistic send paths register
// 'outgoing-new', the socket-accept paths register 'incoming-new'. The render
// coordinator then CONSUMES a registration exactly once when the row first
// commits. History loads, cache restores, pagination, reconnect syncs, ACK
// reconciliation, status/seen updates and re-renders never register — so they
// can never animate. Client-only and ephemeral: cleared at conversation
// boundaries, never persisted, never sent over the wire.
const pendingEntryDirections = new Map(); // stableKey -> 'outgoing-new' | 'incoming-new'
const consumedEntryKeys = new Set();      // once-only guard (echoes, re-renders)
const ENTRY_REGISTRY_CAP = 200;

export function registerEntryAnimation(stableKey, direction) {
  if (!stableKey || (direction !== 'outgoing-new' && direction !== 'incoming-new')) return;
  if (consumedEntryKeys.has(stableKey) || pendingEntryDirections.has(stableKey)) return;
  pendingEntryDirections.set(stableKey, direction);
  if (pendingEntryDirections.size > ENTRY_REGISTRY_CAP) {
    // Bounded hygiene for registrations whose message never rendered.
    const oldest = pendingEntryDirections.keys().next().value;
    pendingEntryDirections.delete(oldest);
  }
  dbg('ENTRY_REGISTER', { key: stableKey, direction });
}

export function consumeEntryAnimation(stableKey) {
  if (!stableKey) return null;
  const direction = pendingEntryDirections.get(stableKey) || null;
  if (direction) {
    pendingEntryDirections.delete(stableKey);
    consumedEntryKeys.add(stableKey);
  }
  return direction;
}

// True while any inner entry animation is still playing (used to keep a typing
// toggle that lands mid-entry from spawning a ghost or rushing the settle).
export function hasActiveEntries() {
  return entryAnims.size > 0;
}

// Cancel everything and leave every element in natural layout. Safe to call at
// any time. Called only at conversation boundaries (switch/unmount), so it also
// resets the entry-eligibility registry for the next conversation.
export function cancelAllChatAnimations() {
  outerAnims.forEach((record) => { try { record.animation.cancel(); } catch { /* noop */ } });
  outerAnims.clear();
  entryAnims.forEach((record) => { try { record.animation.cancel(); } catch { /* noop */ } });
  entryAnims.clear();
  pendingEntryDirections.clear();
  consumedEntryKeys.clear();
  if (activeGhost) {
    try { activeGhost.remove(); } catch { /* noop */ }
    activeGhost = null;
  }
}

// ── FIRST: capture current VISUAL geometry (pure read, animations untouched) ──
export function captureChatLayout(scroller) {
  if (!scroller) return null;
  const rects = new Map();
  const viewportH = (typeof window !== 'undefined' && window.innerHeight) || 0;
  const buffer = CHAT_ANIM.VIEWPORT_BUFFER_PX;
  let rows;
  try { rows = scroller.querySelectorAll(ROW_SELECTOR); } catch { return null; }
  rows.forEach((row) => {
    const key = row.getAttribute('data-chat-layout-row');
    if (!key) return;
    const rect = row.getBoundingClientRect();
    if (rect.bottom >= -buffer && rect.top <= viewportH + buffer) {
      rects.set(key, { top: rect.top, height: rect.height });
    }
  });
  const distanceFromBottom = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
  const snapshot = {
    rects,
    wasNearBottom: distanceFromBottom <= CHAT_ANIM.NEAR_BOTTOM_PX,
  };
  dbg('LAYOUT_CAPTURE', { rows: rects.size, nearBottom: snapshot.wasNearBottom });
  return snapshot;
}

// ── LAST + INVERT + PLAY ─────────────────────────────────────────────────────
// Runs after the DOM committed the new layout. Order:
//  1. cancel in-flight outer FLIPs (natural layout restored, zero residue —
//     the FIRST rects were already captured WITH those transforms, pre-commit);
//  2. if the user was near the bottom pre-mutation, pin the scroll geometry
//     INSTANTLY (never smooth — the FLIP transforms supply the visible motion);
//  3. READ all final natural rects in one pass;
//  4. WRITE all FLIP animations in one pass (translate3d deltaY → 0, no fill).
export function runChatLayoutTransition(scroller, snapshot, { duration = CHAT_ANIM.STACK_MS, easing = CHAT_ANIM.EASING } = {}) {
  const result = { pinned: false, moved: 0 };
  if (!scroller || !snapshot) return result;

  outerAnims.forEach((record) => { try { record.animation.cancel(); } catch { /* noop */ } });
  outerAnims.clear();

  if (snapshot.wasNearBottom) {
    scroller.scrollTop = scroller.scrollHeight - scroller.clientHeight;
    result.pinned = true;
  }

  let rows;
  try { rows = [...scroller.querySelectorAll(ROW_SELECTOR)]; } catch { return result; }

  // READ phase.
  const viewportH = (typeof window !== 'undefined' && window.innerHeight) || 0;
  const buffer = CHAT_ANIM.VIEWPORT_BUFFER_PX;
  const finals = [];
  for (const row of rows) {
    const key = row.getAttribute('data-chat-layout-row');
    if (!key) continue;
    const rect = row.getBoundingClientRect();
    if (rect.bottom < -buffer || rect.top > viewportH + buffer) continue;
    finals.push({ row, key, top: rect.top });
  }

  // WRITE phase.
  for (const { row, key, top } of finals) {
    const before = snapshot.rects.get(key);
    if (!before || !supportsWAAPI(row)) continue;
    const deltaY = before.top - top;
    if (Math.abs(deltaY) < 0.5) continue;
    try {
      makeRecord(outerAnims, row, key, [
        { transform: `translate3d(0, ${deltaY}px, 0)` },
        { transform: 'translate3d(0, 0, 0)' },
      ], { duration, easing }, 'LAYOUT');
      result.moved += 1;
    } catch { /* one row failing must not break the rest */ }
  }

  dbg('LAYOUT_RETARGET', { moved: result.moved, pinned: result.pinned, duration });
  return result;
}

// Accelerate entry animations from PREVIOUS commits so rapid sends never leave
// several half-entered bubbles hovering near the composer. Visual only: the
// older bubbles finish their own path quickly instead of being snapped.
export function accelerateEntriesExcept(currentBatchKeys) {
  let count = 0;
  entryAnims.forEach((record, key) => {
    if (currentBatchKeys && currentBatchKeys.has(key)) return;
    try {
      if (record.animation.playState === 'running' || record.animation.playState === 'pending') {
        record.animation.playbackRate = CHAT_ANIM.ENTRY_RETARGET_RATE;
        count += 1;
      }
    } catch { /* noop */ }
  });
  if (count) dbg('ENTRY_ACCEL', { count });
}

// ── Inner entry: genuinely-new message bubble ────────────────────────────────
// The bubble EMERGES: it appears already recognizable just below its final slot
// (slightly compressed toward its bottom tail corner, slightly faded), rises a
// SHORT clamped distance, and softly settles. It never flies the full distance
// from the composer — the outer stack FLIP communicates the displacement, the
// inner entry communicates the birth. The composer is still measured (fresh, so
// the mobile keyboard state is honored) but only to keep the rise from
// overshooting a bubble that lands right at the composer edge.
// When a same-commit stagger delay is used, a hidden pre-frame keeps the waiting
// bubble invisible (fill:'backwards' shows keyframe[0] during the delay);
// undelayed bubbles start straight at their visible emerge pose — movement and
// growth, not a fade-in.
export function animateMessageEntry(innerEl, key, { side, composerEl, delay = 0 } = {}) {
  if (!supportsWAAPI(innerEl)) return;

  const outgoing = side === 'outgoing';
  const duration = outgoing ? CHAT_ANIM.ENTRY_OUT_MS : CHAT_ANIM.ENTRY_IN_MS;

  let startY = outgoing ? CHAT_ANIM.ENTRY_OUT_START_Y : CHAT_ANIM.ENTRY_IN_START_Y;
  if (outgoing) {
    try {
      const bubbleRect = innerEl.getBoundingClientRect();
      const composerRect = composerEl && composerEl.getBoundingClientRect();
      if (composerRect && bubbleRect.height > 0) {
        const rawY = composerRect.top - bubbleRect.top + 8;
        startY = Math.max(CHAT_ANIM.ENTRY_OUT_START_Y_MIN, Math.min(CHAT_ANIM.ENTRY_OUT_START_Y_MAX, rawY));
      }
    } catch { /* keep fallback */ }
  }

  // Growth anchors at the bubble's tail corner so the scale reads as the bubble
  // materializing from its own edge, not zooming from center. transform-origin
  // lives only inside the keyframes → nothing lingers after the animation.
  // Rapid burst: while an earlier bubble is still entering, followers
  // materialize almost in place — the shared stack glide carries the motion —
  // so a new bubble's down-shifted pose never overlaps the bubble above it,
  // which at that instant is still occupying the gap (FLIP continuity).
  if (entryAnims.size > 0) startY = Math.min(startY, outgoing ? 12 : 10);

  const origin = outgoing ? '100% 100%' : '0% 100%';
  const startScale = outgoing ? CHAT_ANIM.ENTRY_OUT_START_SCALE : CHAT_ANIM.ENTRY_IN_START_SCALE;
  const startOpacity = 0.7;
  const popScale = outgoing ? 1.015 : 1.01;
  const pose = (y, s) => `translate3d(0, ${y}px, 0) scale(${s})`;

  // WhatsApp-2026 style: the bubble rises a touch from below and POPS to size —
  // fade + grow in one eased segment straight to a hair past full size, then a
  // scale-only settle back to 1 (so the segment boundary stays invisible).
  // No lateral travel at all.
  const frames = [];
  if (delay > 0) {
    frames.push({ offset: 0, opacity: 0, transformOrigin: origin, transform: pose(startY, startScale) });
  }
  frames.push(
    { offset: delay > 0 ? 0.02 : 0, opacity: startOpacity, transformOrigin: origin, transform: pose(startY, startScale) },
    { offset: 0.72, opacity: 1, transformOrigin: origin, transform: pose(0, popScale) },
    { offset: 1, opacity: 1, transformOrigin: origin, transform: pose(0, 1) },
  );

  try {
    makeRecord(entryAnims, innerEl, key, frames, {
      duration,
      easing: CHAT_ANIM.EASING,
      delay,
      fill: 'backwards',
    }, 'ENTRY');
    dbg('ENTRY_START', { key, side, delay, startY });
  } catch { /* noop */ }
}

// ── Inner entry: typing indicator appears ────────────────────────────────────
export function animateTypingEntry(innerEl) {
  if (!supportsWAAPI(innerEl)) return;
  try {
    makeRecord(entryAnims, innerEl, TYPING_ROW_KEY, [
      { opacity: 0, transform: 'translate3d(0, 8px, 0) scale(0.96)' },
      { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
    ], { duration: CHAT_ANIM.TYPING_ENTRY_MS, easing: CHAT_ANIM.EASING, fill: 'backwards' }, 'ENTRY');
    dbg('TYPING_INSERT');
  } catch { /* noop */ }
}

// ── Typing exit ghost ────────────────────────────────────────────────────────
// The real typing row is already gone from layout (so the stack settles with
// zero leftover gap); a fixed-position clone fades out in place. It affects no
// layout and no scroll, removes itself, and is cleared by
// cancelAllChatAnimations if the conversation changes mid-fade.
export function spawnTypingExitGhost(ghostNode, rect) {
  if (!ghostNode || !rect || typeof document === 'undefined') return;
  if (!supportsWAAPI(ghostNode)) return;
  try {
    if (activeGhost) { try { activeGhost.remove(); } catch { /* noop */ } }
    ghostNode.style.position = 'fixed';
    ghostNode.style.top = `${rect.top}px`;
    ghostNode.style.left = `${rect.left}px`;
    ghostNode.style.width = `${rect.width}px`;
    ghostNode.style.margin = '0';
    ghostNode.style.pointerEvents = 'none';
    ghostNode.style.zIndex = '10';
    ghostNode.setAttribute('aria-hidden', 'true');
    ghostNode.removeAttribute('data-chat-layout-row');
    document.body.appendChild(ghostNode);
    activeGhost = ghostNode;
    const animation = ghostNode.animate(
      [
        { opacity: 1, transform: 'translate3d(0, 0, 0) scale(1)' },
        { opacity: 0, transform: 'translate3d(0, 6px, 0) scale(0.96)' },
      ],
      { duration: CHAT_ANIM.TYPING_EXIT_MS, easing: 'ease-out' }
    );
    const cleanup = () => {
      try { ghostNode.remove(); } catch { /* noop */ }
      if (activeGhost === ghostNode) activeGhost = null;
    };
    animation.onfinish = cleanup;
    animation.oncancel = cleanup;
    dbg('TYPING_REMOVE');
  } catch {
    try { ghostNode.remove(); } catch { /* noop */ }
    if (activeGhost === ghostNode) activeGhost = null;
  }
}

// Instantly remove the exit ghost (e.g. an incoming bubble is about to occupy
// the typing row's spot — a lingering ghost would overlap it).
export function dismissTypingGhost() {
  if (!activeGhost) return;
  try { activeGhost.remove(); } catch { /* noop */ }
  activeGhost = null;
}
